/**
 * Cookie consent state + pub/sub.
 *
 * We only distinguish two categories:
 *   - essential: login cookies, CSRF, session — always on, never asked
 *   - analytics: PostHog, behavior tracking — requires opt-in
 *
 * Consent is persisted in localStorage under `cookie_consent_v1` as:
 *   { analytics: boolean, timestamp: string }
 *
 * A custom "cookie-consent-changed" event fires on window whenever consent
 * changes. The analytics bootstrap listens so it can initialize or tear
 * down PostHog at runtime without a page reload.
 *
 * NDPR (Nigeria), POPIA (South Africa), and GDPR (EU) all require explicit
 * opt-in before setting non-essential cookies — this module is that gate.
 */

const STORAGE_KEY = 'cookie_consent_v1';
const EVENT_NAME = 'cookie-consent-changed';

export interface ConsentState {
  analytics: boolean;
  timestamp: string; // ISO
}

export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.analytics !== 'boolean') return null;
    return parsed as ConsentState;
  } catch {
    return null;
  }
}

export function setConsent(analytics: boolean): ConsentState {
  const state: ConsentState = {
    analytics,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent<ConsentState>(EVENT_NAME, { detail: state }));
  return state;
}

export function hasAnalyticsConsent(): boolean {
  return getConsent()?.analytics === true;
}

export function onConsentChange(cb: (state: ConsentState) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<ConsentState>).detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
